import { AfterViewInit, Component, inject, ViewChild, ViewContainerRef } from '@angular/core';
import { Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { PopoverModule, PopoverDirective } from "ngx-bootstrap/popover";
import { filter, forEach, get, groupBy, includes, isEmpty, isEqual, orderBy, remove, set, takeRight } from "lodash-es";
import { PageWsService, WmsWorkspaceManagerService } from "@wms/workspace";
import { BaseComponent } from '@wms/components';
import { RouterHistoryService } from "@wms/services";
import { runtimeEventManager } from "@wm/core";


interface Details {
  iconClass: string;
  pageName: string;
  tab: string;
}

interface RecentWSListType {
  url: string;
  _category?: string;
  accessed?: number;
  active: boolean;
  details: Details;
  identifier: string;
  name: string;
  type: string;
}
let isMenuActive: boolean, showMenu: boolean, openTimer: any, closeTimer: any;

let WS_CATEGORIES, isTemplateBundleType;
let WEB_SERVICE_TYPES = ['RESTSERVICE', 'SOAPSERVICE', 'FEEDSERVICE', 'WEBSOCKETSERVICE'];
let LIMIT_BY_CATEGORY: {[key: string]: number} = {
    'PAGE': 9,
    'DB': 3,
    'JAVASERVICE': 3,
    'WEBSERVICE': 3,
    'RESOURCE': 6
  };
let prefix   = 'wms:state';
let KEY;

@Component({
  selector: 'wms-recent-ws-list',
  templateUrl: './wms-recent-ws-list.component.html',
  styleUrls: ['./wms-recent-ws-list.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    PopoverModule
  ],
  styles: [
    `.ws-category-list > .popover-content {
      padding: 0
    }
      .tooltip-content {
      width: max-content;
    }`
  ]
})
export class WmsRecentWsListComponent extends BaseComponent implements AfterViewInit {
  private wsManager: WmsWorkspaceManagerService;
  public activeWS: any = {};
  protected wsCategories: any;
  protected wsList: any;
  private urlHistory: any;
  protected recentWSList: Array<RecentWSListType> = [];
  protected recentWSListByCategory: any = {};

  @ViewChild(PopoverDirective, { static: false }) popoverDirective: PopoverDirective;

  @ViewChild('customTooltipTemplate') customTooltipTemplate: ViewContainerRef;
  private routerHistory: RouterHistoryService;

  constructor(private router: Router, private pageWsService: PageWsService) {
    super();
    this.wsManager = inject(WmsWorkspaceManagerService);
    isTemplateBundleType = this.projectService.isTemplateBundleType();

    this.wsManager.activeWsChangeEvent.asObservable().subscribe((ws: any) => {
      this.activeWS = ws;
      this.setSelectedWSActive();
    });

    WS_CATEGORIES = [
      {
        'id': 'PAGE',
        'displayName': isTemplateBundleType ? this.locale.LABEL_TEMPLATES : this.locale.LABEL_PAGES,
        'icon': isTemplateBundleType ? 'template' : 'page',
        'match': '/page/'
      },
      {
        'id': 'DB',
        'displayName': this.locale.LABEL_DATABASES,
        'icon': 'database'
      },
      {
        'id': 'APISERVICE',
        'displayName': this.locale.LABEL_API_SERVICES,
        'icon': 'api-designer'
      },
      {
        'id': 'JAVASERVICE',
        'displayName': this.locale.LABEL_JAVA_SERVICES,
        'icon': 'java-service'
      },
      {
        'id': 'RESOURCE',
        'displayName': this.locale.LABEL_FILES,
        'icon': 'file'
      }
    ];
    this.wsCategories = WS_CATEGORIES;
    this.wsManager.setWsCategories(this.wsCategories);

    this.eventManager.routeHistoryObservable().subscribe((entry: any) => {
      this.processData(entry);
      this.updateList();
      this.save();
    });

    this.eventManager.setActiveWsObservable().subscribe((ws: any) => {
      if (!ws) {
        this.activeWS = {};
        return;
      }
      if (this.activeWS.name === ws.name && ws.type === this.activeWS.type) {
        if (!this.activeWS.active) {
          this.setSelectedWSActive();
        }
        return;
      }
      if (!ws.details) {
        let details = this.wsCategories.find((wsObj: any) => wsObj.id === ws?._category || wsObj.id === ws?.type);
        ws.details = {icon: !this.activeWS.url?.includes("/rest/") ? details.icon : 'internet'};
      }
      this.activeWS = ws;
      this.setSelectedWSActive();
      this.wsManager.setActiveWS(this.activeWS);
    });

    this.eventManager.canvasDomClickObservable().subscribe(() => {
      this.popoverDirective && this.popoverDirective.hide();
    });

    this.eventManager.updateRouteHistoryObservable().subscribe((ws: any) => {
      this.onDelete(ws);
    });

    this.routerHistory = inject(RouterHistoryService);
    this.getLastSavedState();
    let entries = this.routerHistory.historyEntries;
    if (entries.length) {
      forEach(entries, (entry) => {
        this.processData(entry);
      });
      this.updateList();
    }
  }

  ngAfterViewInit() {
    this.popoverDirective.onShown.subscribe(() => {
      document.body.addEventListener('click', this.onDocClick.bind(this));
    });
    this.popoverDirective.onHidden.subscribe(() => {
      document.body.removeEventListener('click', this.onDocClick.bind(this));
    })
  }

  setSelectedWSActive() {
    if (this.activeWS.url?.includes('?')) {
      this.activeWS.url = this.activeWS.url.split('?')[0];
    }
    let index = this.recentWSList.findIndex((item: any) => (item.active === true));
    if (index > -1) {
      let selectedItem = this.recentWSList[index];
      if (isEqual(selectedItem, this.activeWS) || (selectedItem.name === this.activeWS.name && (selectedItem._category === this.activeWS._category || selectedItem._category?.toLowerCase() === this.activeWS.type?.toLowerCase()) && (selectedItem.url === this.activeWS.url || get(selectedItem, 'path') === this.activeWS.path))) {
        this.activeWS.active = true;
        return;
      }
      set(this.recentWSList[index], 'active', false);
    }
    set(this.activeWS, 'active', true);

    let items = this.recentWSListByCategory[this.activeWS._category] || this.recentWSListByCategory[this.activeWS.type];
    items?.find((item: any) => {
      if (item.active) {
        item.active = false;
        return true;
      }
      return false;
    });
    items?.find((item: any) => {
      if (item.name === this.activeWS.name) {
        if (this.activeWS.hasOwnProperty('url') || this.activeWS.hasOwnProperty('path')) {
          if (this.activeWS._category === 'RESOURCE' ? item.path === this.activeWS.path : item.url === this.activeWS.url) {
            item.active = true;
            return true;
          }
        } else {
          item.active = true;
          return true;
        }
      }
      return false;
    });
  }



  onDocClick(e: any) {
    let $target = $(e.target);
    if (!$target.is('.ws-menu *, .workspace-selection-menu *') && this.popoverDirective.isOpen) {
      this.popoverDirective.hide();
      isMenuActive = false;
    }
  }

  processData(data: any) {
    if (!data.name) {
      return;
    }
    let activeIndex = this.recentWSList.findIndex(ws => ws.active);
    if (activeIndex > -1) {
      this.recentWSList[activeIndex].active = false;
    }
    let existingIndex = this.getWsIndexOf(data);
    if (existingIndex > -1) {
      this.recentWSList[existingIndex]['active'] = true;
      const isActiveExisting = this.getWsIndexOf(this.activeWS) === existingIndex;
      if (isActiveExisting) {
        const details = this.activeWS.details;
        this.recentWSList[existingIndex].details = details;
        return;
      }
      this.activeWS = this.recentWSList[existingIndex];
      return;
    }
    this.validateWSLimit(data);
    if (data.removed) {
      remove(this.recentWSList, (item: any) => item.name === data.removed);
    }
    if (isEqual(data, this.activeWS) || (data.name === this.activeWS.name && (data._category === this.activeWS._category || data._category?.toLowerCase() === this.activeWS.type?.toLowerCase())
      && (data.url === this.activeWS.url || get(data, 'path') === this.activeWS.path))) {
      data.details = this.activeWS?.details;
    }
    if (data.type === 'PAGE' || data._category === 'RESOURCE' || data.type === 'file' || data.type === 'JAVASERVICE' || data.type === 'APISERVICE' ||  data.type === 'DB') {
      this.recentWSList.push(data);
    }
    this.activeWS = data;
  }

  isWebServiceType(type: string) {
    return includes(WEB_SERVICE_TYPES, type);
  }

  onSelect(ws: any) {
    this.popoverDirective.hide();
    let category = ws._category || ws.type;
    if (category === 'RESOURCE') {
      ws.url = '/files';
    }else if (category === 'DB') {
      ws.url = '/database';
    }
    this.wsManager.saveActiveWs.next(() => {
      let index = this.routerHistory.urlHistory.indexOf(ws.url);
      if (index > -1) {
        this.routerHistory.urlHistory.splice(index, 1);
      }
      this.routerHistory.urlHistory.push(ws.url);

      let $promise;
      if (window.location.href.includes('/files?project-id') && category === 'RESOURCE') {
        $promise = Promise.resolve();
      }else if (window.location.href.includes('/database?project-id') && category === 'DB') {
        $promise = Promise.resolve();
      } else {
        $promise = this.router.navigate([ws.url]);
      }

      $promise.then(() => {
        this.eventManager.emitClearActivews('');
        if (category === 'RESOURCE') {
          runtimeEventManager.emit('loadResource', ws);
        } else if (category === 'JAVASERVICE') {
          // TODO: need to be handled in javaservice on navigating to url.
          this.eventManager.emitJavaserviceSelect(ws);
        } else if (category === 'PAGE') {
          const selectedPg = this.routerHistory.urlTypeMap['PAGE'].find((val) => val.split('::')[0] === ws.name);
          const selectedTab = (selectedPg && selectedPg.split('::')[1]) || 'DESIGN';
          if(selectedTab === 'DESIGN'){
            this.pageWsService.isActivetabDesign = selectedTab;
          }else {
            this.pageWsService.isActivetabDesign = '';
            this.pageWsService.selectedTab = selectedTab;
          }
          runtimeEventManager.emit('loadPage', {pageName: ws.name, tab: selectedTab });
          this.pageWsService.setActivePageName(ws.name);
          this.pageWsService.populatePageInfo(ws.name);
        } else if (category === 'APISERVICE') {
          this.eventManager.emitGetLastApiService('');
        } else if(category === 'DB'){
          runtimeEventManager.emit('loadDBServices', ws);
        }
        this.wsManager.setActiveWS(ws);
      });
    });
  }

  getWsIndexOf(data: any) {
    let category = data._category || data.type;
    if (category === 'RESOURCE') {
      return this.recentWSList.findIndex((item: any) => { return item.name === data.name && item._category === data._category && item.path === data.path });
    }
    return this.recentWSList.findIndex((item: any) => { return item.name === data.name && (item._category === data._category || item.type === data.type) });
  }

  validateWSLimit(data: any) {
    if (!data._category) {
      data._category = data.type;
    }
    let addCategory = data._category || data.type;
    let index = this.getWsIndexOf(data);
    if (index > -1) {
      this.recentWSList.splice(index, 1);
      this.recentWSList.push(data);
    } else {
      let removed: any;
      let items = this.recentWSListByCategory[addCategory];
      if (items && items.length === LIMIT_BY_CATEGORY[addCategory]) {
        // map has reached limit, then removing the last accessed i.e. first item in map.
        removed = items.splice(0, 1)[0];
        if (removed) {
          if (removed.path) {
            remove(this.recentWSList, (item: any) => item.name === removed.name && item._category === removed._category && item.path === removed.path);
          } else {
            remove(this.recentWSList, (item: any) => item.name === removed.name && item._category === removed._category);
          }
        }
      }
    }
  }

  getOrderedListByCategory(wsCategory: string) {
    return orderBy(this.recentWSListByCategory[wsCategory], ['accessed'], ['asc']);
  }

  updateList() {
    this.set$Catetory();

    this.recentWSListByCategory = groupBy(this.recentWSList, '_category');

    // this.setActiveWs();


    // $rs.$emit('wms:update-recent-ws-list', recentWSListByCategory);
  }

  save() {
    // KEY = '$' + prefix + '$' + this.projectService.getProjectId() + '$';
    // let openWs = {openWS: this.recentWSList}
    // sessionStorage.setItem(KEY, JSON.stringify(this.recentWSList));
  }

  getLastSavedState() {
    KEY = '$' + prefix + '$' + this.projectService.getProjectId() + '$';
    let lastSavedState =  sessionStorage.getItem(KEY);
    if (lastSavedState) {
      let resourceList = JSON.parse(lastSavedState).openWS;
      resourceList = takeRight(resourceList, LIMIT_BY_CATEGORY['RESOURCE']);
      let filteredEnteries = filter(resourceList, (resource: any) => {
        return this.getWsIndexOf(resource) < 0;
      });
      this.recentWSList = this.recentWSList.concat(filteredEnteries);
    }
    this.updateList();
  }

  setActiveWs() {
    if (!isEmpty(this.activeWS)) {
      let type = this.activeWS.type || this.activeWS._category;
      this.recentWSListByCategory[type]?.find((ws: RecentWSListType) => {
        if (ws.active) {
          ws.active = false;
          return true;
        }
        return false;
      });
      this.recentWSListByCategory[type]?.some((ws: RecentWSListType) => {
        if (ws.type === this.activeWS.type && this.activeWS.name === ws.name) {
          if (ws.type === 'RESOURCE') {
            if (ws.url === this.activeWS.url) { // TODO: need to add check if path is required
              ws.active = true;
              return true;
            }
          } else {
            ws.active = true;
            return true;
          }
        }
        return false;
      });
    }
  }

  set$Catetory() {
    forEach(this.recentWSList, (wsInfo) => {
      let category = wsInfo._category;

      if (!category) {
        category = wsInfo.type;
        if (category.toLowerCase() === 'file' || category.toLowerCase() === 'folder') {
          category = 'RESOURCE';
        }
        if (this.isWebServiceType(category)) {
          category = 'WEBSERVICE';
        }

        wsInfo._category = category;
      }
    });
  }

  onPopOverMouseEnter($event: any) {
    isMenuActive = true;
  }

  hideTooltiponleave() {
    this.popoverDirective.hide();
  }

  onDelete(ws: any) {
    let i = this.recentWSList.findIndex((item: any) => (item.name === ws.name && (item._category === ws._category || item.type.toLowerCase() === ws.type.toLowerCase())));
    if ( i > -1 ) {
      let deletedEntry: any = this.recentWSList.splice(i, 1)[0];
      if (deletedEntry.active) {
        this.activeWS = {};
      }
      this.routerHistory.removeFromHistory(deletedEntry.type || deletedEntry._category, deletedEntry.name, deletedEntry.path || deletedEntry.url);
      this.recentWSListByCategory = groupBy(this.recentWSList, '_category');
    }
  }
}
